import type { ReactNode } from "react";
import React, { useState } from "react";
import { Field, hasSubmitFailed, submit as submitForm } from "redux-form";

import { Typography } from "@braintrust/braintrust-ui-components";
import {
  useBeginPasskeyStepUpMutation,
  useCompletePasskeyStepUpMutation,
} from "@js/apps/auth/api";
import { MFAMethodSelection } from "@js/apps/common/forms/fields/otp/mfa-method-selection";
import {
  useHasPasskeyEnabled,
  useIsPasskeyOnlyUser,
} from "@js/apps/common/hooks/use-has-passkey-enabled";
import type { ModalInstanceProps } from "@js/components/modal";
import { Modal, ModalConfirm, ModalInstance } from "@js/components/modal";
import { createFormInstance } from "@js/forms/components";
import { CheckboxField } from "@js/forms/fields/checkbox";
import { useAppDispatch, useAppSelector } from "@js/hooks";
import { capitalize } from "@js/utils/string";

import { OTPAuthCodeField } from "./otp-auth-code";
import {
  useWebAuthnAssertion,
  WebAuthnAssertionPrompt,
} from "./webauthn-assertion";

import styles from "./styles.module.scss";

export type OTPFieldValueType = {
  code?: string;
  is_backup_code?: boolean;
  key?: string;
};

/**
 * Normalizes an OTPForm/createOTPModalInstance submission into whichever proof the backend
 * expects - a passkey step-up key, or the manually-typed code/backup-code pair.
 */
export const otpValuesToConfirmation = ({
  code,
  is_backup_code,
  key,
}: OTPFieldValueType):
  { key: string } | { code: string; is_backup_code: boolean } =>
  key ? { key } : { code: code as string, is_backup_code: !!is_backup_code };

const OTP_DESCRIPTION_ID = "otp-description";

export type ReduxFormFieldInput = { onChange: (value: string) => void };

/**
 * A hidden redux-form field that exposes its `input.onChange` via `onReady`, so a component
 * outside the normal typing flow (the passkey step-up ceremony) can still write into this
 * ambient form's `key` field without needing to know the form's name.
 */
export const HiddenKeyField = ({
  onReady,
}: {
  onReady: (input: ReduxFormFieldInput, formName: string) => void;
}) => (
  <Field
    name="key"
    component={({
      input,
      meta,
    }: {
      input: ReduxFormFieldInput;
      meta?: { form: string };
    }) => {
      if (meta) {
        onReady(input, meta.form);
      }
      return null;
    }}
  />
);

type PasskeyStepUpFieldProps = {
  onCancel: () => void;
  cancelLabel: string;
  autoStart?: boolean;
};

/**
 * Runs the passkey step-up ceremony and writes the resulting key into the ambient form's
 * hidden `key` field, then submits the form - the same path a manually-typed `code` follows.
 */
export const PasskeyStepUpField = ({
  onCancel,
  cancelLabel,
  autoStart,
}: PasskeyStepUpFieldProps) => {
  const dispatch = useAppDispatch();
  const [beginPasskeyStepUp] = useBeginPasskeyStepUpMutation();
  const [completePasskeyStepUp] = useCompletePasskeyStepUpMutation();
  const [isSubmittingKey, setIsSubmittingKey] = useState(false);
  const formNameRef = React.useRef<string | null>(null);
  const submitFailed = useAppSelector((state) =>
    formNameRef.current ? hasSubmitFailed(formNameRef.current)(state) : false,
  );
  const { authenticate, isAuthenticating, error } = useWebAuthnAssertion({
    begin: () => beginPasskeyStepUp().unwrap(),
    complete: (assertion) => completePasskeyStepUp(assertion).unwrap(),
  });
  const keyInputRef = React.useRef<ReduxFormFieldInput | null>(null);

  React.useEffect(() => {
    if (submitFailed) {
      setIsSubmittingKey(false);
    }
  }, [submitFailed]);

  const handleAuthenticate = async () => {
    const result = await authenticate();
    if (result && formNameRef.current) {
      setIsSubmittingKey(true);
      keyInputRef.current?.onChange(result.key);
      const formName = formNameRef.current;
      setTimeout(() => dispatch(submitForm(formName)), 0);
    }
  };

  return (
    <>
      <HiddenKeyField
        onReady={(input, name) => {
          keyInputRef.current = input;
          formNameRef.current = name;
        }}
      />
      <WebAuthnAssertionPrompt
        error={error}
        isAuthenticating={isAuthenticating || isSubmittingKey}
        onAuthenticate={handleAuthenticate}
        onCancel={onCancel}
        cancelLabel={cancelLabel}
        autoStart={autoStart}
      />
    </>
  );
};

export type CreateModalInstanceReturnType<Type extends string> = {
  [K in `${"open" | "close"}${Capitalize<Type>}`]: () => void;
} & {
  [K in `${Capitalize<Type>}Instance`]: () => ReactNode;
};

/**
 * @param name "OTPModal" suffix is required
 * @example
 * const {
 *   openExampleOTPModal,
 *   closeExampleOTPModal,
 *   ExampleOTPModalInstance,
 * } = createOTPModalInstance("exampleOTPModal");
 */
export const createOTPModalInstance = <T extends `${string}OTPModal`>(
  name: T,
  modalProps?: ModalInstanceProps,
): CreateModalInstanceReturnType<T> => {
  const result = createOTPModal({
    name,
    modalProps,
    createModalContent: _createOTPAuthVerificationModal,
  });

  return result as CreateModalInstanceReturnType<T>;
};

type CreateOTPModalType<T extends `${string}OTPModal`> = {
  name: T;
  modalProps?: ModalInstanceProps;
  createModalContent: (
    OTPModalInstance: ReturnType<typeof Modal>,
  ) => () => React.ReactNode;
};

export const createOTPModal = <T extends `${string}OTPModal`>({
  name,
  modalProps,
  createModalContent,
}: CreateOTPModalType<T>) => {
  const OTPModalInstance = Modal(name, {
    className: styles.modal,
    closeButton: false,
    disablePortal: true,
    ...(modalProps || {}),
  });

  const { open, close } = OTPModalInstance;

  const capitalizedName = capitalize(name);

  const result: Record<string, unknown> = {};

  result[`open${capitalizedName}`] = open;
  result[`close${capitalizedName}`] = close;
  result[`${capitalizedName}Instance`] = createModalContent(OTPModalInstance);

  return result as CreateModalInstanceReturnType<T>;
};

export const MODAL_MODE = {
  SELECT: "select",
  CODE: "code",
  PASSKEY: "passkey",
} as const;

type ModalMode = ObjectValues<typeof MODAL_MODE>;

export const useInitialMFAMode = () => {
  const hasPasskey = useHasPasskeyEnabled();
  const isPasskeyOnly = useIsPasskeyOnlyUser();
  const bothAvailable = hasPasskey && !isPasskeyOnly;

  const [mode, setMode] = useState<ModalMode>(() => {
    if (bothAvailable) {
      return MODAL_MODE.SELECT;
    }
    return isPasskeyOnly ? MODAL_MODE.PASSKEY : MODAL_MODE.CODE;
  });

  return { mode, setMode, bothAvailable };
};

const OTPOrPasskeyModalBody = ({ onCancel }: { onCancel: () => void }) => {
  const { mode, setMode, bothAvailable } = useInitialMFAMode();
  const [autoStartPasskey, setAutoStartPasskey] = useState(false);

  if (mode === MODAL_MODE.SELECT) {
    return (
      <MFAMethodSelection
        onSelectOtp={() => setMode(MODAL_MODE.CODE)}
        onSelectPasskey={() => {
          setAutoStartPasskey(true);
          setMode(MODAL_MODE.PASSKEY);
        }}
        onCancel={onCancel}
      />
    );
  }

  if (mode === MODAL_MODE.PASSKEY) {
    return (
      <PasskeyStepUpField
        onCancel={bothAvailable ? () => setMode(MODAL_MODE.SELECT) : onCancel}
        cancelLabel={bothAvailable ? "Use different method" : "Cancel"}
        autoStart={autoStartPasskey}
      />
    );
  }

  return (
    <OTPModalContent
      onCancel={bothAvailable ? () => setMode(MODAL_MODE.SELECT) : onCancel}
      cancelText={bothAvailable ? "Use different method" : undefined}
    >
      <OTPAuthCodeField
        inputProps={{
          "aria-describedby": OTP_DESCRIPTION_ID,
          autoFocus: true,
        }}
      />
      <Field
        name="is_backup_code"
        component={CheckboxField}
        label="Use backup code instead"
      />
    </OTPModalContent>
  );
};

const _createOTPAuthVerificationModal =
  (ModalComponent: ReturnType<typeof Modal>) => (): React.ReactNode => {
    return (
      <ModalComponent>
        <OTPOrPasskeyModalBody onCancel={ModalComponent.close} />
      </ModalComponent>
    );
  };

export type ModalContentProps = {
  children: React.ReactNode;
  onCancel: () => void;
  cancelText?: string;
};

type OTPModalContentBaseProps = ModalContentProps & {
  title: string;
  description: string;
  descriptionId: string;
};

const OTPModalContentBase = ({
  title,
  description,
  descriptionId,
  children,
  onCancel,
  cancelText,
}: OTPModalContentBaseProps) => {
  return (
    <>
      <div id={descriptionId}>
        <Typography
          component="h4"
          variant="title"
          fontWeight={400}
          sx={{ mb: 2 }}
        >
          {title}
        </Typography>
        <Typography component="p" sx={{ mb: 4 }}>
          {description}
        </Typography>
      </div>
      <ModalConfirm
        onCancel={onCancel}
        cancelText={cancelText}
        onConfirm="submit"
        confirmText="Verify"
        buttonsSquareShape
        confirmButtonVariant="positive-2"
        buttonsOnEnds
      >
        {children}
      </ModalConfirm>
    </>
  );
};

export const OTPModalContent = ({
  children,
  onCancel,
  cancelText,
}: ModalContentProps) => {
  return (
    <OTPModalContentBase
      title="2-step authentication"
      description="Enter the 6-digit code generated by your authenticator app."
      descriptionId={OTP_DESCRIPTION_ID}
      onCancel={onCancel}
      cancelText={cancelText}
    >
      {children}
    </OTPModalContentBase>
  );
};

type OTPAuthVerificationModalProps = {
  onSubmit:
    | ((values: OTPFieldValueType) => void)
    | ((values: OTPFieldValueType) => Promise<void>);
  onSubmitSuccess?: () => void;
};

type OTPFormProps = OTPAuthVerificationModalProps & {
  onClose: () => void;
  onSubmitFail?: () => void;
};

const initialValues: { is_backup_code: boolean } = { is_backup_code: false };

const OTPFormInstance = createFormInstance<OTPFieldValueType, unknown>(
  "otp-auth-verification-modal-form",
);

const OTPForm = ({
  onSubmit,
  onSubmitSuccess,
  onClose,
  onSubmitFail = () => {},
}: OTPFormProps) => {
  const { mode, setMode, bothAvailable } = useInitialMFAMode();
  const [autoStartPasskey, setAutoStartPasskey] = useState(false);

  if (mode === MODAL_MODE.SELECT) {
    return (
      <MFAMethodSelection
        onSelectOtp={() => setMode(MODAL_MODE.CODE)}
        onSelectPasskey={() => {
          setAutoStartPasskey(true);
          setMode(MODAL_MODE.PASSKEY);
        }}
        onCancel={onClose}
      />
    );
  }

  if (mode === MODAL_MODE.PASSKEY) {
    return (
      <PasskeyStepUpSubmit
        onCancel={bothAvailable ? () => setMode(MODAL_MODE.SELECT) : onClose}
        cancelLabel={bothAvailable ? "Use different method" : "Cancel"}
        onSubmit={onSubmit}
        onSubmitSuccess={onSubmitSuccess}
        onSubmitFail={onSubmitFail}
        autoStart={autoStartPasskey}
      />
    );
  }

  return (
    <OTPFormInstance
      onSubmit={onSubmit}
      onSubmitFail={onSubmitFail}
      onSubmitSuccess={onSubmitSuccess}
      initialValues={initialValues}
    >
      <OTPModalContent
        onCancel={bothAvailable ? () => setMode(MODAL_MODE.SELECT) : onClose}
        cancelText={bothAvailable ? "Use different method" : undefined}
      >
        <OTPAuthCodeField
          inputProps={{
            "aria-describedby": OTP_DESCRIPTION_ID,
            autoFocus: true,
          }}
        />
        <Field
          name="is_backup_code"
          component={CheckboxField}
          label="Use backup code instead"
        />
      </OTPModalContent>
    </OTPFormInstance>
  );
};

type PasskeyStepUpSubmitProps = {
  onCancel: () => void;
  cancelLabel: string;
  onSubmit: OTPAuthVerificationModalProps["onSubmit"];
  onSubmitSuccess?: () => void;
  onSubmitFail?: () => void;
  autoStart?: boolean;
};

/**
 * Passkey counterpart of OTPForm's code path - runs the ceremony then calls the caller's
 * onSubmit directly with the resulting key, since there's no ambient redux-form to submit into.
 */
export const PasskeyStepUpSubmit = ({
  onCancel,
  cancelLabel,
  onSubmit,
  onSubmitSuccess,
  onSubmitFail = () => {},
  autoStart,
}: PasskeyStepUpSubmitProps) => {
  const [beginPasskeyStepUp] = useBeginPasskeyStepUpMutation();
  const [completePasskeyStepUp] = useCompletePasskeyStepUpMutation();
  const [isSubmittingKey, setIsSubmittingKey] = useState(false);
  const { authenticate, isAuthenticating, error } = useWebAuthnAssertion({
    begin: () => beginPasskeyStepUp().unwrap(),
    complete: (assertion) => completePasskeyStepUp(assertion).unwrap(),
  });

  const handleAuthenticate = async () => {
    const result = await authenticate();
    if (!result) {
      return;
    }
    setIsSubmittingKey(true);
    try {
      await onSubmit({ key: result.key });
      onSubmitSuccess?.();
    } catch (_e) {
      setIsSubmittingKey(false);
      onSubmitFail?.();
    }
  };

  return (
    <WebAuthnAssertionPrompt
      error={error}
      isAuthenticating={isAuthenticating || isSubmittingKey}
      onAuthenticate={handleAuthenticate}
      onCancel={onCancel}
      cancelLabel={cancelLabel}
      autoStart={autoStart}
    />
  );
};

/**
 * @summary Avoid using this function from within form as it will not propagate backend response to the original form. When responses for both OTP and form's onSubmit need to be handled use createOTPModalInstance @see createOTPModalInstance
 */
export const submitActionAfterOTPConfirmation = ({
  onSubmit,
  onSubmitSuccess,
}: OTPAuthVerificationModalProps) => {
  return ModalInstance.open({
    className: styles.modal,
    closeButton: false,
    children: (
      <OTPForm
        onClose={ModalInstance.close}
        onSubmit={onSubmit}
        onSubmitSuccess={onSubmitSuccess || ModalInstance.close}
      />
    ),
  });
};
